import { AutoAwesome, ExpandMore, List, Search } from '@mui/icons-material';
import { Button, ButtonGroup, Menu, MenuItem } from '@mui/material';

import { useCurrentUserContext } from '@/modules/auth/hooks/useCurrentUserContext';
import { PageHeader } from '@/modules/common/components/PageHeader';
import { TableFilter } from '@/modules/common/components/TableFilter';
import { CoursesSearchParams } from '@/routes/_admin/courses';
import TabContext from '@mui/lab/TabContext';
import TabList from '@mui/lab/TabList';
import TabPanel from '@mui/lab/TabPanel';
import { TextField } from '@mui/material';
import Tab from '@mui/material/Tab';
import { keepPreviousData } from '@tanstack/react-query';
import { Link, getRouteApi } from '@tanstack/react-router';
import { PaginationState } from '@tanstack/react-table';
import { useState } from 'react';
import { ListCoursesTable } from '../components/ListCoursesTable/ListCoursesTable';
import { useGetCategories } from '../hooks/useCategories';
import { useGetCourses } from '../hooks/useCourses';

const routeApi = getRouteApi('/_admin/courses/');
const PAGE_SIZE = 50;

export function ListCoursesPage() {
  const search = routeApi.useSearch() as CoursesSearchParams;
  const navigate = routeApi.useNavigate();
  const [searchQuery, setSearchQuery] = useState(search.term || '');
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const {
    currentUser: { educationProvider = '' } = {},
    isLoading: isLoadingUser,
  } = useCurrentUserContext() ?? {};

  const {
    isLoading,
    data: { data: courses = [], metadata: { total = 0, page = 1 } = {} } = {},
  } = useGetCourses(
    {
      educationProvider: educationProvider as string,
      ...(search.term && { term: search.term }),
      ...(search.category && { categories: [search.category as string] }),
      ...(search.published !== undefined && { published: search.published }),
    },
    {
      page: search.page,
      limit: PAGE_SIZE,
    },
    {
      updatedAt: 'desc',
    },
    {
      enabled: !isLoadingUser && !!educationProvider,
      placeholderData: keepPreviousData,
    },
  );

  const { data: { data: categories = [] } = {} } = useGetCategories();

  const paginationState = {
    pageIndex: (page ?? 1) - 1 || 0,
    pageSize: PAGE_SIZE,
  };

  const onPaginationChange = ({ pageIndex }: PaginationState) => {
    navigate({
      search: (prev: Record<string, unknown>) => ({
        ...prev,
        page: pageIndex + 1,
      }),
    });
  };

  const showClearFilters =
    Object.entries(search).filter(([key]) => key !== 'page').length > 0;

  const handleSearch = (value: string) => {
    setSearchQuery(value);
    navigate({
      search: {
        ...search,
        term: value,
      },
    });
  };

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  return (
    <div className="relative flex flex-col h-auto min-h-full">
      <TabContext value="courses">
        <header className="sticky top-0 w-full z-30 bg-white @container">
          <PageHeader pageTitle="Your courses">
            <ButtonGroup
              disableElevation
              variant="contained"
              aria-label="add course button group"
            >
              <Button
                startIcon={<AutoAwesome />}
                href="/courses/add?enableAi=true"
                variant="contained"
                size="small"
                className="rounded-r-none border-lines"
              >
                Add course
              </Button>
              <Button
                size="small"
                onClick={handleClick}
                aria-controls={open ? 'add-course-menu' : undefined}
                aria-haspopup="true"
                aria-expanded={open ? 'true' : undefined}
                className="rounded-l-none  border-dividers"
              >
                <ExpandMore />
              </Button>
            </ButtonGroup>
            <Menu
              id="add-course-menu"
              anchorEl={anchorEl}
              open={open}
              onClose={handleClose}
              slotProps={{
                list: {
                  'aria-labelledby': 'add-course-button',
                },
              }}
            >
              <Link to="/courses/add" className="no-underline">
                <MenuItem href="/courses/add" onClick={handleClose}>
                  Add course manually
                </MenuItem>
              </Link>
            </Menu>
          </PageHeader>

          <TabList className="px-8 border-b mt-8">
            <Tab
              value="courses"
              label={
                <div className="flex gap-2">
                  <List />
                  Courses list
                </div>
              }
            />
          </TabList>
        </header>
        <TabPanel value="courses">
          <nav className="flex gap-12 px-8 py-3">
            <TableFilter
              label="Category"
              value={search.category}
              onChange={e => {
                navigate({
                  search: prev => ({
                    ...prev,
                    category: e.target.value as string,
                    page: 1,
                  }),
                });
              }}
              renderValue={selected => {
                if (!selected) return 'All';
                return (
                  categories.find(category => category._id === selected)
                    ?.name ?? 'All'
                );
              }}
              displayEmpty
            >
              <MenuItem value="">All</MenuItem>
              {categories.map(category => (
                <MenuItem key={category._id} value={category._id}>
                  {category.name}
                </MenuItem>
              ))}
            </TableFilter>

            <div className="flex items-center">
              <TableFilter
                label="Status"
                value={search.published}
                onChange={e => {
                  navigate({
                    search: prev => ({
                      ...prev,
                      published:
                        e.target.value === 'all'
                          ? undefined
                          : e.target.value === 'true',
                      page: 1,
                    }),
                  });
                }}
                renderValue={selected => {
                  if (selected === undefined) return 'All';
                  return selected ? 'Published' : 'Unpublished';
                }}
                displayEmpty
              >
                <MenuItem value="all">All</MenuItem>
                <MenuItem value="true">Published</MenuItem>
                <MenuItem value="false">Unpublished</MenuItem>
              </TableFilter>
            </div>
            {showClearFilters && (
              <div className="flex items-center">
                <Link
                  className="text-xs font-medium text-mydra-purple"
                  search={{
                    page: 1,
                  }}
                >
                  Clear
                </Link>
              </div>
            )}
            <div className="flex items-center ml-auto">
              <TextField
                size="small"
                value={searchQuery}
                onChange={e => handleSearch(e.target.value)}
                placeholder="Search courses..."
                fullWidth
                slotProps={{
                  input: {
                    startAdornment: <Search className="text-gray-400" />,
                  },
                }}
                aria-label="Search courses"
                hiddenLabel
              />
            </div>
          </nav>
          <div className="flex-1 px-8">
            <ListCoursesTable
              isLoading={isLoading}
              onPaginationChange={updater => {
                return typeof updater === 'function'
                  ? onPaginationChange(updater(paginationState))
                  : onPaginationChange(updater);
              }}
              courses={courses}
              paginationState={paginationState}
              rowCount={total}
            />
          </div>
        </TabPanel>
      </TabContext>
    </div>
  );
}
